import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import {
  Popover,
  Box,
  MenuItem,
  ListItemText,
  ListItemIcon,
  Radio,
} from "@mui/material";

const NestedOptionsPanel = ({
  open,
  anchorEl,
  onClose,
  options = [],
  selectedValue,
  onSelect,
  vertical = "bottom",
}) => {
  const selectedItemRef = useRef(null);

  const handleClick = (value) => {
    if (onSelect) {
      onSelect(value);
    }
  };

  useEffect(() => {
    if (open && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open, selectedValue, options]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      disableAutoFocus
      disableEnforceFocus
      sx={{ pointerEvents: "none" }}
      anchorOrigin={{
        vertical: vertical,
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          sx: {
            pointerEvents: "auto",
            backgroundColor: "#FFFFFF",
            borderRadius: "10px",
            boxShadow: "0px 4px 16px rgba(0,0,0,0.12)",
            minWidth: 220,
            maxWidth: 260,
            mt: 0.5,
          },
        },
      }}
    >
      <Box
        sx={{
          p: "8px",
          maxHeight: 320,
          overflowY: "auto",
        }}
        role="menu"
        aria-label="Nested options"
      >
        {options.map((opt, idx) => {
          const isSelected = selectedValue === opt;
          const baseLabelId = `nested-option-text-${idx}`;

          return (
            <MenuItem
              ref={isSelected ? selectedItemRef : null}
              key={opt}
              role="menuitemradio"
              onClick={() => handleClick(opt)}
              sx={{
                width: "100%",
                minHeight: 32,
                height: "auto",
                justifyContent: "space-between",
                p: 0,
                pt: "6px",
                pr: "12px",
                pb: "6px",
                pl: "12px",
                borderRadius: "4px",
                backgroundColor: "#FFFFFF",
                mb: 0.5,
                "&:hover": {
                  backgroundColor: "#F2F2F2",
                },
                cursor: "pointer",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "space-between",
                }}
              >
                <ListItemText
                  primary={opt}
                  slotProps={{
                    primary: {
                      id: baseLabelId,
                      component: "span",
                      sx: {
                        color: "#0A0A0A",
                        fontSize: "12px",
                        fontWeight: isSelected ? 600 : 400,
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                      },
                    },
                  }}
                />
                <ListItemIcon
                  sx={{
                    minWidth: 24,
                    justifyContent: "flex-end",
                    display: "flex",
                  }}
                >
                  <Radio
                    checked={isSelected}
                    sx={{
                      p: 0,
                      color: "#403C6F",
                      "& .MuiSvgIcon-root": {
                        fontSize: 18,
                      },
                      "&.Mui-checked": {
                        color: "#403C6F !important",
                      },
                    }}
                    slotProps={{
                      input: {
                        "aria-labelledby": baseLabelId,
                      },
                    }}
                  />
                </ListItemIcon>
              </Box>
            </MenuItem>
          );
        })}
      </Box>
    </Popover>
  );
};

NestedOptionsPanel.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.any,
  onClose: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.string),
  selectedValue: PropTypes.string,
  onSelect: PropTypes.func,
  vertical: PropTypes.oneOf(["top", "bottom"]),
};

export default NestedOptionsPanel;
